<template>
    <div class="hero-banner">
        <!-- Fixed Banner Background -->
        <div class="hero-desert-bg-container">
            <!-- Video background (optional: add src/assets/img/banner/bg-video.mp4 to enable) -->
            <video
                v-if="bannerVideoSrc"
                class="hero-bg-video"
                autoplay
                muted
                loop
                playsinline
                :src="bannerVideoSrc"
            />
            <div class="hero-desert-bg bg-active"
                :style="{ backgroundImage: `url(${bannerImage})` }">
                <div class="desert-sun"></div>
                <!-- Text Overlay with Button -->
                <div class="hero-banner-text-wrapper">
                    <div class="hero-banner-text">
                        <h1 class="hero-title">
                            <span class="hero-title-line">World Cup 2026</span>
                            <span class="hero-title-line">Fever Is Live</span>
                        </h1>
                        <p class="hero-subtitle">Play Football-Themed Casino Games on worldcupsports</p>
                        <button class="hero-play-button" @click="handlePlayNowClick">GET STARTED</button>
                    </div>
                </div>
            </div>
        </div>

        <!-- Content Overlay -->
        <div class="hero-overlay">
            <div class="hero-content">
                <div class="hero-left">
                    <div class="hero-glow-circle"></div>
                </div>
            </div>
        </div>

        <!-- Sign In Modal -->
        <SignInModal :show="showSignInModal" @close="showSignInModal = false" />
    </div>
</template>

<script>
import { mapGetters } from 'vuex';
import SignInModal from '@/components/SignInModal.vue';

export default {
    name: 'HeroBanner',
    components: {
        SignInModal
    },
    data() {
        return {
            showSignInModal: false,
            bannerImage: require('@/assets/img/banner/theTitle.png'),
            // Set to a video path to use video background (e.g. require('@/assets/img/banner/bg-video.mp4'))
            bannerVideoSrc: null
        }
    },
    computed: {
        ...mapGetters(['authUser'])
    },
    methods: {
        handlePlayNowClick() {
            // If user is not logged in, show sign in modal
            if (this.authUser.user === null) {
                this.showSignInModal = true;
            } else {
                // If user is logged in, navigate to games
                this.$router.push('/our-casino');
            }
        }
    }
}
</script>

<style scoped>
.hero-banner {
    position: relative;
    padding: var(--spacing-lg);
    overflow: visible;
    min-height: 380px;
    max-height: 420px;
    display: flex;
    align-items: center;
    justify-content: center;
}

/* Desert Background Container */
.hero-desert-bg-container {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    z-index: 1;
    overflow: hidden;
    border-radius: 25px;
}

/* Video background layer – sits behind image; add bg-video.mp4 and set bannerVideoSrc to enable */
.hero-bg-video {
    position: absolute;
    top: 50%;
    left: 50%;
    min-width: 100%;
    min-height: 100%;
    width: auto;
    height: auto;
    transform: translate(-50%, -50%);
    object-fit: cover;
    z-index: 0;
}

/* Desert Background (image – fallback or overlay on video) */
.hero-desert-bg {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    z-index: 1;
    background-size: cover;
    background-position: center;
    background-repeat: no-repeat;
}

/* Deep gradient overlay at bottom - transitions to page background */
.hero-desert-bg::after {
    content: '';
    position: absolute;
    bottom: 0;
    left: 0;
    right: 0;
    height: 20%;
    background: linear-gradient(
        to bottom,
        transparent 0%,
        rgba(21, 10, 46, 0.3) 30%,
        rgba(21, 10, 46, 0.75) 60%,
        var(--bg-purple-dark) 100%
    );
    pointer-events: none;
}

@media only screen and (max-width: 768px) {
    .hero-desert-bg {
        background-size: cover;
        background-position: center center;
    }
}




/* Hero Banner Text Overlay Wrapper */
.hero-banner-text-wrapper {
    position: absolute;
    left: var(--spacing-2xl);
    top: 50%;
    transform: translateY(-50%);
    z-index: 10;
    width: auto;
    max-width: 60%;
    pointer-events: auto;
}

/* Hero Banner Text Overlay (with button) */
.hero-banner-text-wrapper .hero-banner-text {
    display: flex;
    flex-direction: column;
    gap: var(--spacing-md);
    pointer-events: auto;
}

@media only screen and (max-width: 1024px) {
    .hero-banner-text-wrapper .hero-banner-text {
        gap: var(--spacing-sm);
    }
}

@media only screen and (max-width: 768px) {
    .hero-banner-text-wrapper .hero-banner-text {
        gap: 4px;
    }
}

@media only screen and (max-width: 480px) {
    .hero-banner-text-wrapper .hero-banner-text {
        gap: 2px;
    }
}

.hero-title {
    font-family: 'Anton SC', sans-serif;
    font-weight: 400;
    font-style: normal;
    font-size: 64px;
    line-height: 69px;
    letter-spacing: 0.02em;
    margin: 0;
    padding: 0;
    /* Gradient with a bright streak for video-style shimmer */
    background: linear-gradient(
        105deg,
        #FFF8E7 0%,
        #FFE4A0 20%,
        #E8C547 35%,
        #FFFBF0 45%,
        #E8C547 55%,
        #D4A574 70%,
        #C9A227 100%
    );
    background-size: 200% auto;
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    background-clip: text;
    display: flex;
    flex-direction: column;
    gap: 4px;
    filter: drop-shadow(0 2px 4px rgba(0, 0, 0, 0.6)) drop-shadow(0 4px 12px rgba(0, 0, 0, 0.4)) drop-shadow(0 0 24px rgba(201, 162, 39, 0.35));
    -webkit-text-stroke: 1px rgba(0, 0, 0, 0.25);
    paint-order: stroke fill;
    animation: hero-title-glow 4s ease-in-out infinite, hero-title-shimmer 5s ease-in-out infinite;
}

@keyframes hero-title-glow {
    0%, 100% { filter: drop-shadow(0 2px 4px rgba(0, 0, 0, 0.6)) drop-shadow(0 4px 12px rgba(0, 0, 0, 0.4)) drop-shadow(0 0 24px rgba(201, 162, 39, 0.35)); }
    50% { filter: drop-shadow(0 2px 4px rgba(0, 0, 0, 0.6)) drop-shadow(0 4px 14px rgba(0, 0, 0, 0.45)) drop-shadow(0 0 36px rgba(201, 162, 39, 0.5)); }
}

/* Video-style: moving shine across the text */
@keyframes hero-title-shimmer {
    0% { background-position: 0% center; }
    100% { background-position: 200% center; }
}

.hero-title-line {
    display: block;
}

.hero-subtitle-wrapper {
    display: flex;
    flex-direction: column;
    gap: 8px;
    margin-top: var(--spacing-lg);
}

.hero-subtitle-header {
    color: #ffffff;
    font-family: 'Satoshi', sans-serif;
    font-weight: 700;
    font-style: normal;
    font-size: 20px;
    line-height: 1.4;
    letter-spacing: 0.5px;
    margin: 0;
    margin-bottom: 4px;
    padding: 0;
}

.hero-subtitle {
    color: #F5E6C8;
    font-family: 'Satoshi', sans-serif;
    font-weight: 500;
    font-style: normal;
    font-size: 20px;
    line-height: 1.5;
    letter-spacing: 0%;
    margin: 0;
    padding: 0;
    text-shadow: 0 1px 3px rgba(0, 0, 0, 0.5), 0 0 20px rgba(212, 165, 116, 0.15);
}

.hero-number {
    font-family: 'Space Grotesk', monospace;
    font-weight: 700;
    letter-spacing: 0.5px;
}

.hero-subtitle-2 {
    color: #ffffff;
    font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif;
    font-weight: 400;
    font-style: normal;
    font-size: 16px;
    line-height: 25px;
    letter-spacing: 0%;
    margin: 0;
    margin-top: var(--spacing-md);
    padding: 0;
}

@media only screen and (max-width: 1024px) {
    .hero-subtitle-wrapper {
        margin-top: var(--spacing-md);
        gap: 6px;
    }

    .hero-subtitle-header {
        font-size: 16px;
        margin-bottom: 2px;
    }

    .hero-subtitle,
    .hero-subtitle-2 {
        font-size: 14px;
        line-height: 1.4;
    }
}

@media only screen and (max-width: 768px) {
    .hero-subtitle-wrapper {
        margin-top: var(--spacing-sm);
        gap: 4px;
    }

    .hero-subtitle-header {
        font-size: 14px;
        margin-bottom: 2px;
    }

    .hero-subtitle,
    .hero-subtitle-2 {
        font-size: 12px;
        line-height: 1.4;
    }
}

@media only screen and (max-width: 480px) {
    .hero-subtitle-wrapper {
        margin-top: var(--spacing-sm);
        gap: 3px;
    }

    .hero-subtitle-header {
        font-size: 12px;
        margin-bottom: 1px;
    }

    .hero-subtitle,
    .hero-subtitle-2 {
        font-size: 10px;
        line-height: 1.3;
    }
}

.desert-sun {
    position: absolute;
    top: -100px;
    right: -100px;
    width: 400px;
    height: 400px;
    border-radius: 50%;
    filter: blur(20px);
}


@keyframes sunGlow {
    0% {
        transform: scale(1);
        opacity: 0.8;
    }

    100% {
        transform: scale(1.1);
        opacity: 1;
    }
}

/* Overlay */
.hero-overlay {
    position: relative;
    z-index: 3;
    width: 100%;
    height: 100%;
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: var(--spacing-2xl);
    pointer-events: none;
}

.hero-content {
    display: flex;
    width: 100%;
    align-items: center;
    justify-content: space-between;
    gap: var(--spacing-2xl);
}

.hero-left {
    flex: 1;
    position: relative;
    display: flex;
    align-items: center;
    justify-content: flex-start;
    min-height: 300px;
    padding-left: var(--spacing-2xl);
}

.hero-glow-circle {
    width: 300px;
    height: 300px;
    border-radius: 50%;
    filter: blur(40px);
    position: absolute;
    left: 0;
}


@keyframes pulse {

    0%,
    100% {
        transform: scale(1);
        opacity: 0.6;
    }

    50% {
        transform: scale(1.2);
        opacity: 0.8;
    }
}

.hero-right {
    flex: 1;
    display: flex;
    flex-direction: column;
    align-items: flex-end;
    gap: var(--spacing-xl);
    text-align: right;
    padding: 318px 77px 0 0;
}

@media only screen and (max-width: 1024px) {
    .hero-right {
        padding: 0;
        align-items: center;
        justify-content: center;
    }
}

.hero-play-button {
    padding: var(--spacing-lg) var(--spacing-2xl);
    background: var(--accent-btn-purple);
    color: #ffffff;
    border: none;
    border-radius: var(--radius-md);
    font-weight: 800;
    font-size: 24px;
    text-transform: uppercase;
    letter-spacing: 2px;
    cursor: pointer;
    transition: all 0.3s ease;
    box-shadow:
        0 8px 25px rgba(222, 199, 156, 0.5),
        0 0 30px rgba(222, 199, 156, 0.3),
        inset 0 2px 10px rgba(255, 255, 255, 0.3);
    position: relative;
    overflow: hidden;
    width: fit-content;
    margin-top: var(--spacing-md);
    align-self: flex-start;
    pointer-events: auto;
    z-index: 15;
}

@media only screen and (max-width: 1024px) {
    .hero-play-button {
        margin-top: var(--spacing-sm);
    }
}

@media only screen and (max-width: 768px) {
    .hero-play-button {
        margin-top: 4px;
    }
}

@media only screen and (max-width: 480px) {
    .hero-play-button {
        margin-top: 2px;
    }
}

.hero-play-button::before {
    content: '';
    position: absolute;
    top: 0;
    left: -100%;
    width: 100%;
    height: 100%;
    background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.4), transparent);
    transition: left 0.5s ease;
}

.hero-play-button:hover {
    transform: scale(1.05) translateY(-2px);
    box-shadow:
        0 12px 35px rgba(222, 199, 156, 0.7),
        0 0 50px rgba(222, 199, 156, 0.5),
        inset 0 2px 10px rgba(255, 255, 255, 0.4);
}

.hero-play-button:hover::before {
    left: 100%;
}

.hero-play-button:active {
    transform: scale(1.02) translateY(0);
}

@media only screen and (max-width: 1024px) {
    .hero-banner {
        min-height: 350px;
    }

    .hero-content {
        flex-direction: column;
        gap: var(--spacing-lg);
        justify-content: center;
    }

    .hero-left {
        min-height: 150px;
        justify-content: center;
        padding-left: 0;
    }

    .hero-glow-circle {
        width: 200px;
        height: 200px;
        left: 50%;
        transform: translateX(-50%);
    }

    .hero-play-button {
        padding: var(--spacing-sm) var(--spacing-lg);
        font-size: 16px;
        margin-top: var(--spacing-sm);
    }

    .hero-title {
        font-size: 36px;
    }


    .hero-subtitle {
        font-size: 13px;
        line-height: 18px;
        margin-top: var(--spacing-sm);
    }

    .hero-subtitle-2 {
        font-size: 13px;
        line-height: 18px;
        margin-top: var(--spacing-sm);
    }

    .hero-banner-text-wrapper {
        left: var(--spacing-lg);
        max-width: 70%;
    }
}

@media only screen and (max-width: 768px) {
    .hero-banner {
        margin: 40px var(--spacing-md) var(--spacing-md) var(--spacing-md);
        padding: var(--spacing-md);
        min-height: 280px;
        max-height: 400px;
    }

    .hero-desert-bg-container {
        min-height: 280px;
    }

    .hero-overlay {
        padding: var(--spacing-lg) var(--spacing-md);
        min-height: 280px;
    }

    .hero-content {
        flex-direction: column;
        gap: var(--spacing-md);
        justify-content: center;
        align-items: center;
        width: 100%;
    }

    .hero-left {
        min-height: 100px;
        width: 100%;
        justify-content: center;
        padding-left: 0;
    }

    .hero-play-button {
        padding: 6px var(--spacing-md);
        font-size: 12px;
        letter-spacing: 0.5px;
        width: auto;
        min-width: 100px;
        margin-top: 4px;
        align-self: flex-start;
    }

    .hero-glow-circle {
        width: 120px;
        height: 120px;
        left: 50%;
        transform: translateX(-50%);
    }

    .hero-title {
        font-size: 28px;
    }

    .hero-banner-text-wrapper {
        left: var(--spacing-md);
        max-width: 80%;
    }

    .hero-banner-text {
        display: flex;
        flex-direction: column;
        gap: 2px;
    }

    .desert-sun {
        display: none;
    }

}

@media only screen and (max-width: 480px) {
    .hero-banner {
        margin: 30px var(--spacing-sm) var(--spacing-sm) var(--spacing-sm);
        padding: var(--spacing-sm);
        min-height: 250px;
        max-height: 350px;
    }

    .hero-desert-bg-container {
        min-height: 250px;
    }

    .hero-overlay {
        padding: var(--spacing-md);
        min-height: 250px;
    }

    .hero-content {
        gap: var(--spacing-sm);
    }

    .hero-left {
        min-height: 80px;
    }

    .hero-play-button {
        padding: 4px var(--spacing-sm);
        font-size: 10px;
        letter-spacing: 0.5px;
        min-width: 80px;
        margin-top: 2px;
    }

    .hero-glow-circle {
        width: 100px;
        height: 100px;
    }

    .hero-title {
        font-size: 22px;
    }

    .hero-banner-text-wrapper {
        left: var(--spacing-sm);
        max-width: 85%;
    }

    .hero-banner-text {
        display: flex;
        flex-direction: column;
        gap: 2px;
    }

}
</style>
